import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Label,
  Row,
} from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { useNavigate } from "react-router-dom"
import * as Yup from "utils/yup"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import {
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import ConfirmModal from "components/Common/ConfirmModal"

const initialValues = {
  earningSharePercent: 80,
}

const DeliveryPartnerSettings = () => {
  const { t } = useTranslation()
  document.title = t("Delivery Partner Settings")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, isLoading, deliveryPartnerSettings } = useSelector(
    state => state.GlobalSettings
  )
  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })
  const [formValues, setFormValues] = useState(initialValues)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [pendingValues, setPendingValues] = useState(null)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      47,
      "delivery_partners_6",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      47,
      "delivery_partners_2",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole, accountType, navigate])

  useEffect(() => {
    fetchGlobalSettings()
  }, [])

  useEffect(() => {
    if (deliveryPartnerSettings?.deliveryPartnerSettings) {
      const { earningSharePercent } =
        deliveryPartnerSettings.deliveryPartnerSettings
      setFormValues({
        earningSharePercent: earningSharePercent ?? 80,
      })
    }
  }, [deliveryPartnerSettings])

  const fetchGlobalSettings = () => {
    dispatch(getGlobalSettingByType({ type: "deliveryPartnerSettings" }))
  }

  const validationSchema = Yup.object({
    earningSharePercent: Yup.number()
      .required(t("Rider earning share is required"))
      .min(0, t("Minimum is 0%"))
      .max(100, t("Maximum is 100%")),
  })

  const handleOnSubmit = values => {
    setPendingValues(values)
    setShowSaveModal(true)
  }

  const confirmSave = () => {
    if (!pendingValues) return
    const { earningSharePercent } = pendingValues
    const payload = {
      id: deliveryPartnerSettings._id,
      type: "deliveryPartnerSettings",
      deliveryPartnerSettings: {
        earningSharePercent: Number(earningSharePercent),
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowSaveModal(false)
          fetchGlobalSettings()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleOnSubmit}
        >
          {() => (
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <CardTitle>{t("Delivery Partner Settings")}</CardTitle>
                    <p className="text-muted">
                      {t(
                        "Configure how much of the order total is credited to the rider when a delivery is completed."
                      )}
                    </p>
                    <Form>
                      <Row className="mb-4">
                        <Col md={12}>
                          <div className="form_field">
                            <Label htmlFor="earningSharePercent">
                              <span className="text-danger">*</span>
                              {t("Rider earning share (%)")}
                            </Label>
                            <Field
                              name="earningSharePercent"
                              type="number"
                              min={0}
                              max={100}
                              step="0.01"
                              className="form-control"
                              disabled={!usersPermissions.edit}
                            />
                            <ErrorMessage
                              name="earningSharePercent"
                              component={TextError}
                            />
                            <small className="text-muted">
                              {t(
                                "e.g. 80 means the rider earns 80% of the order total on delivery. Applies to new deliveries only."
                              )}
                            </small>
                          </div>
                        </Col>
                      </Row>

                      {usersPermissions.edit && (
                        <div className="d-flex justify-content-end">
                          <Button
                            color="primary"
                            type="submit"
                            disabled={isLoading}
                          >
                            {isLoading ? t("Saving...") : t("Save")}
                          </Button>
                        </div>
                      )}
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Formik>

        <ConfirmModal
          show={showSaveModal}
          onDeleteClick={confirmSave}
          onCloseClick={() => setShowSaveModal(false)}
          isLoading={isLoading}
          buttonText={t("Yes")}
          bodyTitle={t(
            "Are you sure you want to save the delivery partner settings?"
          )}
        />
      </div>
    </div>
  )
}

export default DeliveryPartnerSettings
